<template>
  <div class="login-page">
    <el-form
      ref="pwdForm"
      :model="form"
      :rules="rules"
      class="login-form-wrap"
      @submit.native.prevent
    >
      <div class="login-content">
        <h1 class="login-title">
          {{ pageTitle }}
        </h1>
        <p class="login-subtitle">{{ pageSubtitle }}</p>
        <div class="login-form">
          <el-form-item v-if="isPublicMode" prop="username">
            <el-input
              v-model="form.username"
              placeholder="用户名"
              size="large"
              clearable
              class="login-input"
              autocomplete="username"
              @keyup.enter.native="handleSubmit"
            >
              <i slot="prefix" class="el-input__icon el-icon-user"></i>
            </el-input>
          </el-form-item>
          <el-form-item v-else>
            <el-input
              :value="storedUsername"
              disabled
              size="large"
              class="login-input"
            >
              <i slot="prefix" class="el-input__icon el-icon-user"></i>
            </el-input>
          </el-form-item>
          <el-form-item v-if="!isPublicMode" prop="oldPassword">
            <el-input
              v-model="form.oldPassword"
              type="password"
              placeholder="当前密码"
              size="large"
              show-password
              clearable
              class="login-input"
              autocomplete="current-password"
              @keyup.enter.native="handleSubmit"
            >
              <i slot="prefix" class="el-input__icon el-icon-lock"></i>
            </el-input>
          </el-form-item>
          <el-form-item prop="newPassword">
            <el-input
              v-model="form.newPassword"
              type="password"
              placeholder="新密码"
              size="large"
              show-password
              clearable
              class="login-input"
              autocomplete="new-password"
              @keyup.enter.native="handleSubmit"
            >
              <i slot="prefix" class="el-input__icon el-icon-lock"></i>
            </el-input>
          </el-form-item>
          <el-form-item prop="confirmPassword">
            <el-input
              v-model="form.confirmPassword"
              type="password"
              placeholder="确认新密码"
              size="large"
              show-password
              clearable
              class="login-input"
              autocomplete="new-password"
              @keyup.enter.native="handleSubmit"
            >
              <i slot="prefix" class="el-input__icon el-icon-lock"></i>
            </el-input>
          </el-form-item>
          <el-form-item v-if="isPublicMode" prop="email">
            <el-input
              v-model.trim="form.email"
              placeholder="邮箱地址"
              size="large"
              clearable
              class="login-input"
            >
              <i slot="prefix" class="el-input__icon el-icon-message"></i>
            </el-input>
          </el-form-item>
          <el-form-item v-if="isPublicMode" prop="emailCode" class="register-code-item">
            <div class="register-code-row">
              <el-input
                v-model.trim="form.emailCode"
                placeholder="邮箱验证码"
                size="large"
                clearable
                class="login-input register-code-input"
                @keyup.enter.native="handleSubmit"
              >
                <i slot="prefix" class="el-input__icon el-icon-key"></i>
              </el-input>
              <el-button
                type="primary"
                class="register-send-code-btn"
                :disabled="!canSendCode"
                :loading="sendCodeLoading"
                @click="handleSendEmailCode"
              >
                {{ sendCodeText }}
              </el-button>
            </div>
          </el-form-item>
          <el-form-item>
            <el-button
              type="primary"
              class="login-btn"
              :loading="loading"
              @click="handleSubmit"
            >
              <i v-if="!loading" class="el-icon-right login-btn__icon"></i>
              {{ isPublicMode ? '重置密码' : '确认修改' }}
            </el-button>
          </el-form-item>
          <p class="register-to-login">
            <router-link v-if="isPublicMode" class="login-agree-link" to="/login">
              返回登录
            </router-link>
            <router-link v-else class="login-agree-link" to="/profile">
              返回我的
            </router-link>
          </p>
        </div>
      </div>
      <div v-if="isPublicMode" class="login-agree-outer">
        <el-form-item prop="agreed" class="login-agree-item">
          <el-checkbox v-model="form.agreed">
            <span class="login-agree-text">我已阅读并同意</span>
            <router-link
              class="login-agree-link"
              to="/user-agreement"
              @click.native.stop
            >《用户协议》</router-link>
            <span class="login-agree-text">与</span>
            <router-link
              class="login-agree-link"
              to="/privacy-policy"
              @click.native.stop
            >《隐私政策》</router-link>
          </el-checkbox>
        </el-form-item>
      </div>
    </el-form>
  </div>
</template>

<script>
import axios from 'axios'
import { getApiErrorMessage } from '@/utils/apiErrorMessage'
import { clearAuthState, getStoredUserProfile } from '@/utils/userSession'

export default {
  name: 'ChangePasswordPage',
  data() {
    return {
      form: {
        username: '',
        email: '',
        emailCode: '',
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
        agreed: false
      },
      loading: false,
      sendCodeLoading: false,
      sendCodeCooldown: 0,
      sendCodeTimer: null
    }
  },
  computed: {
    isPublicMode() {
      return this.$route.meta.publicChangePassword === true
    },
    pageTitle() {
      return this.isPublicMode ? '忘记密码' : '修改密码'
    },
    pageSubtitle() {
      return this.isPublicMode
        ? '输入账号名，直接设置新登录密码（无需旧密码）'
        : '为当前账号设置新登录密码'
    },
    storedUsername() {
      const profile = getStoredUserProfile()
      return (profile.userName && String(profile.userName).trim()) || ''
    },
    canSendCode() {
      if (!this.isPublicMode) return false
      const username = (this.form.username || '').trim()
      const email = (this.form.email || '').trim()
      const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
      return !!username && emailOk && !this.sendCodeLoading && this.sendCodeCooldown === 0
    },
    sendCodeText() {
      if (this.sendCodeCooldown > 0) return `${this.sendCodeCooldown}s后重发`
      return '发送验证码'
    },
    rules() {
      const validateConfirm = (_, value, callback) => {
        const s = value != null ? String(value) : ''
        if (!s) {
          callback(new Error('请再次输入新密码'))
          return
        }
        if (s !== this.form.newPassword) {
          callback(new Error('两次输入的新密码不一致'))
          return
        }
        callback()
      }
      const validateStrongPassword = (_, value, callback) => {
        const s = value != null ? String(value) : ''
        if (!s) {
          callback(new Error('请输入新密码'))
          return
        }
        callback()
      }
      const passwordFields = {
        newPassword: [
          { validator: validateStrongPassword, trigger: ['blur', 'change'] }
        ],
        confirmPassword: [{ validator: validateConfirm, trigger: ['blur', 'change'] }]
      }
      if (this.isPublicMode) {
        return {
          username: [
            { required: true, message: '请输入用户名', trigger: 'blur' },
            { min: 2, max: 64, message: '长度为 2～64 个字符', trigger: 'blur' }
          ],
          email: [
            { required: true, message: '请输入邮箱地址', trigger: 'blur' },
            { type: 'email', message: '请输入正确的邮箱地址', trigger: ['blur', 'change'] }
          ],
          emailCode: [{ required: true, message: '请输入邮箱验证码', trigger: 'blur' }],
          agreed: [
            {
              validator: (_, value, callback) => {
                if (!value) {
                  callback(new Error('请阅读并同意用户协议和隐私政策'))
                } else {
                  callback()
                }
              },
              trigger: 'change'
            }
          ],
          ...passwordFields
        }
      }
      return {
        oldPassword: [{ required: true, message: '请输入当前密码', trigger: 'blur' }],
        ...passwordFields
      }
    }
  },
  mounted() {
    if (this.isPublicMode) {
      return
    }
    if (!this.storedUsername) {
      this.$message.warning('未获取到用户信息，请重新登录')
      this.$router.replace('/login')
    }
  },
  beforeDestroy() {
    if (this.sendCodeTimer) {
      window.clearInterval(this.sendCodeTimer)
      this.sendCodeTimer = null
    }
  },
  methods: {
    startSendCodeCooldown(seconds = 60) {
      this.sendCodeCooldown = seconds
      if (this.sendCodeTimer) {
        window.clearInterval(this.sendCodeTimer)
      }
      this.sendCodeTimer = window.setInterval(() => {
        if (this.sendCodeCooldown <= 1) {
          window.clearInterval(this.sendCodeTimer)
          this.sendCodeTimer = null
          this.sendCodeCooldown = 0
          return
        }
        this.sendCodeCooldown -= 1
      }, 1000)
    },
    async handleSendEmailCode() {
      if (!this.isPublicMode) return
      const username = (this.form.username || '').trim()
      const email = (this.form.email || '').trim()
      if (!username) {
        this.$message.warning('请先输入用户名')
        return
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        this.$message.warning('请输入正确的邮箱地址')
        return
      }
      this.sendCodeLoading = true
      try {
        await axios.post(
          '/api/app/email/send-code',
          { username, email, type: 2 },
          { skipAuth: true }
        )
        this.$message.success('验证码发送成功，请查收邮箱')
        this.startSendCodeCooldown(60)
      } catch (error) {
        this.$message.error(getApiErrorMessage(error, '验证码发送失败，请稍后重试'))
      } finally {
        this.sendCodeLoading = false
      }
    },
    effectiveUsername() {
      if (this.isPublicMode) {
        return (this.form.username || '').trim()
      }
      return this.storedUsername
    },
    /** 提为组件方法，避免嵌套回调内调用导致 eslint 误判 import 未使用 */
    afterPasswordChanged() {
      clearAuthState()
      this.$router.replace('/login')
    },
    handleSubmit() {
      this.$refs.pwdForm.validate(async (valid) => {
        if (!valid) return
        const username = this.effectiveUsername()
        if (!username) {
          this.$message.error(this.isPublicMode ? '请输入用户名' : '未获取到用户名')
          return
        }

        this.loading = true
        try {
          if (this.isPublicMode) {
            /** 忘记密码：仅用户名 + 新密码（与后端 resetPassword 约定一致） */
            await axios.post(
              '/api/app/core/user/resetPassword',
              {
                username,
                email: (this.form.email || '').trim(),
                verificationCode: (this.form.emailCode || '').trim(),
                newPassword: this.form.newPassword
              },
              { skipAuth: true }
            )
            this.$message.success('密码已重置，请使用新密码登录')
          } else {
            await axios.post('/api/app/core/user/changePassword', {
              username,
              oldPassword: this.form.oldPassword,
              newPassword: this.form.newPassword
            })
            this.$message.success('密码已更新，请使用新密码登录')
          }
          this.afterPasswordChanged()
        } catch (error) {
          console.error('修改密码失败:', error)
          this.$message.error(
            getApiErrorMessage(error, this.isPublicMode ? '重置失败，请重试' : '修改失败，请重试')
          )
        } finally {
          this.loading = false
        }
      })
    }
  }
}
</script>

<style scoped>
.login-page {
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f2f6ff;
  padding-top: env(safe-area-inset-top, 0px);
  padding-bottom: env(safe-area-inset-bottom, 0px);
  padding-left: max(32px, env(safe-area-inset-left, 0px));
  padding-right: max(32px, env(safe-area-inset-right, 0px));
  box-sizing: border-box;
}

.login-form-wrap {
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
}

.login-content {
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
  padding: 40px 36px 44px;
  box-sizing: border-box;
  background: #ffffff;
  border-radius: 18px;
  border: 1px solid rgba(10, 28, 68, 0.06);
  box-shadow:
    0 4px 24px rgba(10, 28, 68, 0.06),
    0 1px 3px rgba(10, 28, 68, 0.04);
  animation: loginReveal 0.5s ease forwards;
}

@keyframes loginReveal {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.login-title {
  margin: 0 0 10px;
  font-size: 32px;
  font-weight: 600;
  color: #0a1c44;
  letter-spacing: 0.02em;
}

.login-subtitle {
  margin: 0 0 36px;
  font-size: 15px;
  color: #6b7a94;
  font-weight: 400;
  line-height: 1.5;
}

.login-form >>> .el-form-item {
  margin-bottom: 24px;
}

.login-form >>> .el-form-item:last-of-type {
  margin-bottom: 0;
  margin-top: 8px;
}

.register-to-login {
  margin: 18px 0 0;
  text-align: center;
  font-size: 14px;
  color: #6b7a94;
  line-height: 1.5;
}

.login-agree-outer {
  margin-top: 20px;
  padding: 0 4px;
}

.login-agree-item {
  margin-bottom: 0 !important;
}

.login-agree-item >>> .el-form-item__content {
  line-height: 1.55;
}

.login-agree-item >>> .el-checkbox {
  align-items: flex-start;
  white-space: normal;
}

.login-agree-item >>> .el-checkbox__label {
  font-size: 13px;
  color: #6b7a94;
  line-height: 1.55;
  padding-left: 8px;
}

.login-agree-link {
  color: #4253ff;
  text-decoration: none;
  font-weight: 500;
}

.login-agree-link:hover {
  text-decoration: underline;
}

.login-agree-text {
  color: #6b7a94;
}

.login-input >>> .el-input__inner {
  height: 50px;
  line-height: 50px;
  padding-left: 42px;
  border-radius: 12px;
  background: #f4f6fa;
  border: 1px solid rgba(10, 28, 68, 0.08);
  font-size: 14px;
  color: #0a1c44;
  transition: border-color 0.25s, box-shadow 0.25s, background 0.25s;
}

.login-input >>> .el-input__inner:hover {
  background: #eef1f6;
  border-color: rgba(10, 28, 68, 0.12);
}

.login-input >>> .el-input__prefix {
  left: 12px;
  color: #8b95a8;
}

.login-input >>> .el-input__prefix .el-input__icon {
  font-size: 18px;
  line-height: 50px;
}

.login-input >>> .el-input__inner::placeholder {
  font-size: 14px;
  color: #9aa5b8;
}

.login-input >>> .el-input__inner:focus {
  background: #ffffff;
  border-color: #4253ff;
  box-shadow: 0 0 0 3px rgba(66, 83, 255, 0.12);
}

.register-code-item {
  margin-bottom: 10px;
}

.register-code-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.register-code-input {
  flex: 1;
}

.register-send-code-btn {
  flex: 0 0 auto;
  min-width: 116px;
  height: 50px;
  border-radius: 12px;
  padding: 0 12px;
}

.login-input.is-disabled >>> .el-input__inner {
  color: #6b7a94;
  background: #eef1f6;
  border-color: rgba(10, 28, 68, 0.08);
}

.login-btn {
  width: 100%;
  min-height: 50px;
  height: 50px;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.12em;
  border-radius: 12px;
  border: none !important;
  background: linear-gradient(135deg, #4253ff 0%, #3546e8 100%) !important;
  color: #fff !important;
  transition: transform 0.2s, box-shadow 0.2s, opacity 0.2s;
  -webkit-tap-highlight-color: transparent;
}

.login-btn:hover,
.login-btn:focus {
  background: linear-gradient(135deg, #5364ff 0%, #4253ff 100%) !important;
  color: #fff !important;
}

.login-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 20px rgba(66, 83, 255, 0.35);
}

.login-btn:active {
  transform: translateY(0);
}

.login-btn__icon {
  margin-right: 8px;
  font-size: 16px;
  vertical-align: middle;
}

@media (max-width: 768px) {
  .login-page {
    padding-top: max(20px, env(safe-area-inset-top, 0px));
    padding-bottom: max(20px, env(safe-area-inset-bottom, 0px));
    padding-left: max(28px, env(safe-area-inset-left, 0px));
    padding-right: max(28px, env(safe-area-inset-right, 0px));
    align-items: center;
    justify-content: center;
  }

  .login-content {
    max-width: 100%;
    padding: 28px 22px 32px;
    border-radius: 16px;
  }

  .login-title {
    font-size: 26px;
  }

  .login-subtitle {
    font-size: 14px;
    margin-bottom: 28px;
  }

  .login-form >>> .el-form-item {
    margin-bottom: 20px;
  }

  .login-input >>> .el-input__inner {
    height: 48px;
    line-height: 48px;
    padding-left: 40px;
    font-size: 14px;
  }

  .login-input >>> .el-input__prefix .el-input__icon {
    line-height: 48px;
    font-size: 17px;
  }

  .login-btn {
    min-height: 48px;
    height: 48px;
  }

  .register-send-code-btn {
    height: 48px;
    min-width: 104px;
    font-size: 13px;
  }
}

@media (max-width: 375px) {
  .login-content {
    padding: 24px 18px 28px;
  }

  .login-title {
    font-size: 24px;
  }
}
</style>
